import { useId, useMemo, useState } from 'react';
import { CHROME } from './palette';
import { formatMkd } from '../../lib/format';

/**
 * Two-or-more series over months. Replaces the LiveCharts LineSeries on the
 * desktop dashboard.
 *
 * Plain SVG rather than a chart library: the whole thing is ~150 lines, it has
 * no dependency to keep current, and it inherits the page's responsive layout
 * for free.
 */

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

const PAD = { top: 16, right: 16, bottom: 34, left: 68 };
const HEIGHT = 260;
const WIDTH = 720; // viewBox units; the SVG scales to its container

export default function LineChart({
  labels,
  series,
  ariaLabel,
}: {
  labels: string[];
  series: LineSeries[];
  ariaLabel: string;
}) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const { max, ticks } = useMemo(() => {
    const allValues = series.flatMap((s) => s.values);
    const rawMax = Math.max(1, ...allValues);
    // Round the axis up to something readable rather than the exact peak.
    const magnitude = 10 ** Math.floor(Math.log10(rawMax));
    const niceMax = Math.ceil(rawMax / magnitude) * magnitude;
    return {
      max: niceMax,
      ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => niceMax * f),
    };
  }, [series]);

  if (labels.length === 0) {
    return (
      <div className="flex h-52 items-center justify-center text-sm text-muted">
        Nuk ka të dhëna për këtë periudhë.
      </div>
    );
  }

  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  // A single point would divide by zero; place it mid-plot instead.
  const x = (i: number) =>
    PAD.left + (labels.length === 1 ? plotW / 2 : (i / (labels.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;

  // Show at most ~8 x labels so they never collide.
  const labelStep = Math.max(1, Math.ceil(labels.length / 8));

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={ariaLabel}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`${gradientId}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Gridlines and y ticks */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke={CHROME.grid}
              strokeWidth="1"
            />
            <text
              x={PAD.left - 8}
              y={y(t) + 4}
              textAnchor="end"
              fontSize="11"
              fill={CHROME.muted}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {Math.round(t).toLocaleString('sq-MK')}
            </text>
          </g>
        ))}

        {/* Baseline */}
        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={y(0)}
          y2={y(0)}
          stroke={CHROME.axis}
          strokeWidth="1"
        />

        {/* x labels. The first and last sit on the plot edges, so centering
            them pushes half the text outside the viewBox — anchor them inward. */}
        {labels.map((label, i) =>
          i % labelStep === 0 ? (
            <text
              key={label + i}
              x={x(i)}
              y={HEIGHT - 12}
              textAnchor={i === 0 ? 'start' : i === labels.length - 1 ? 'end' : 'middle'}
              fontSize="11"
              fill={CHROME.muted}
            >
              {label}
            </text>
          ) : null,
        )}

        {/* Area fills and lines */}
        {series.map((s) => {
          const line = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
          const area = `${line} L ${x(s.values.length - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`;
          return (
            <g key={s.key}>
              <path d={area} fill={`url(#${gradientId}-${s.key})`} />
              <path
                d={line}
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </g>
          );
        })}

        {/* Crosshair */}
        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={PAD.top}
            y2={y(0)}
            stroke={CHROME.axis}
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        )}

        {/* Markers, drawn with a surface ring so overlapping points stay legible */}
        {series.map((s) =>
          s.values.map((v, i) =>
            hover === i || labels.length <= 12 ? (
              <circle
                key={`${s.key}-${i}`}
                cx={x(i)}
                cy={y(v)}
                r={hover === i ? 5 : 3.5}
                fill={s.color}
                stroke={CHROME.surface}
                strokeWidth="2"
              />
            ) : null,
          ),
        )}

        {/* Hit targets, wider than the marks but clamped to the plot: the end
            points sit on the edges, so an uncentred band would reach outside
            the chart and swallow pointer events from the page. */}
        {labels.map((_, i) => {
          const band = plotW / Math.max(1, labels.length);
          const left = Math.max(PAD.left, x(i) - band / 2);
          const right = Math.min(WIDTH - PAD.right, x(i) + band / 2);
          return (
            <rect
              key={i}
              x={left}
              y={PAD.top}
              width={Math.max(0, right - left)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          );
        })}
      </svg>

      {/* Legend — always present for 2+ series, so identity is never colour-alone */}
      <div className="mt-2 flex flex-wrap items-center gap-4">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-muted">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: s.color }}
              aria-hidden="true"
            />
            {s.label}
          </span>
        ))}
      </div>

      {/* Values for the hovered month, in ink rather than series colour */}
      {hover !== null && (
        <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <span className="font-medium">{labels[hover]}</span>
          {series.map((s) => (
            <span key={s.key} className="ml-4 text-muted">
              {s.label}:{' '}
              <span className="tnum font-medium text-ink">{formatMkd(s.values[hover] ?? 0)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
